import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Globe, Search, FileText, ExternalLink, Copy, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEOAdmin = () => {
  const [dns, setDns] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    axios.get(`${API}/seo/dns-instructions`, { withCredentials: true })
      .then(({ data }) => setDns(data))
      .catch(() => {});
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!dns) return <div className="text-center py-8 text-zinc-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="seo-admin">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-400" /> SEO & Domain Setup
        </h2>
      </div>

      {/* Current Status */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 text-center">
            <Search className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-white">{dns.current_seo_status?.pages_indexed}</p>
            <p className="text-[10px] text-zinc-500">Pages in Sitemap</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 text-center">
            <FileText className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-white">Active</p>
            <p className="text-[10px] text-zinc-500">robots.txt</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 text-center">
            <Globe className="w-5 h-5 text-orange-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-white">{dns.domain}</p>
            <p className="text-[10px] text-zinc-500">Domain</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm">Live SEO Files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[
            { label: "Sitemap", url: dns.current_seo_status?.sitemap },
            { label: "Robots.txt", url: dns.current_seo_status?.robots },
          ].map(f => (
            <div key={f.label} className="flex items-center justify-between bg-zinc-800 rounded p-2">
              <span className="text-xs text-zinc-300">{f.label}</span>
              <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                {f.url} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* DNS Instructions */}
      {dns.instructions?.map((step, i) => (
        <Card key={i} className="bg-zinc-900 border-zinc-800" data-testid={`dns-step-${step.step}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Badge className="bg-orange-900/30 text-orange-400 text-[10px]">Step {step.step}</Badge>
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-zinc-400 mb-2">{step.howto}</p>
            {step.type && (
              <div className="bg-zinc-800 rounded p-3 flex items-center justify-between">
                <div className="font-mono text-xs">
                  <span className="text-zinc-500">Type: </span><span className="text-green-400">{step.type}</span>
                  <span className="text-zinc-500 ml-3">Name: </span><span className="text-blue-400">{step.name}</span>
                  <span className="text-zinc-500 ml-3">Value: </span><span className="text-amber-400">{step.value}</span>
                </div>
                <button onClick={() => copy(step.value, `step-${step.step}`)} className="text-zinc-500 hover:text-white">
                  {copied === `step-${step.step}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
            {step.records && (
              <div className="space-y-1.5 mt-2">
                {step.records.map((rec, j) => (
                  <div key={j} className="bg-zinc-800 rounded p-2 font-mono text-xs">
                    <span className="text-zinc-500">Type: </span><span className="text-green-400">{rec.type}</span>
                    <span className="text-zinc-500 ml-3">Name: </span><span className="text-blue-400">{rec.name}</span>
                    <span className="text-zinc-500 ml-3">Value: </span><span className="text-amber-400">{rec.value}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SEOAdmin;
